/**
 * Landing chooser — pick which console to sign into. The vendor-console app
 * hosts both the Vendor onboarding flow and the Pet-Owner (user) console;
 * this screen routes to the matching OTP login.
 */
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Moon, PawPrint, Store, Sun, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { use_theme_store } from '@/lib/theme-store';

function ChoiceCard({
  icon: Icon,
  title,
  description,
  cta,
  onClick,
  testid,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  cta: string;
  onClick: () => void;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="group clay-press flex flex-1 flex-col items-start gap-4 rounded-card border-2 border-brand-outline-variant/20 bg-card p-6 text-left shadow-clay transition-all duration-normal ease-bounce hover:-translate-y-1 hover:border-primary/40 hover:shadow-clay-lg"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-button accent-gradient text-white shadow-clay-sm">
        <Icon className="h-6 w-6" />
      </div>
      <div>
        <h2 className="font-heading text-xl font-bold">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      </div>
      <span className="mt-auto pt-2 text-sm font-semibold text-primary group-hover:underline">
        {cta} →
      </span>
    </button>
  );
}

export function ChooserPage() {
  const navigate = useNavigate();
  const dark = use_theme_store((s) => s.dark);
  const toggle_theme = use_theme_store((s) => s.toggle);

  return (
    <div className="page-gradient relative flex min-h-svh flex-col items-center justify-center p-5">
      <Button
        variant="ghost"
        size="sm"
        className="absolute left-4 top-4 gap-1.5"
        onClick={() => navigate('/')}
        data-testid="chooser-back-home"
      >
        <ArrowLeft className="h-4 w-4" />
        Home
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="absolute right-4 top-4"
        onClick={toggle_theme}
        aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
        data-testid="theme-toggle"
      >
        {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>

      <div className="w-full max-w-2xl">
        <div className="mb-10 flex flex-col items-center gap-3 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-button accent-gradient text-white shadow-clay">
            <PawPrint className="h-7 w-7" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Pet Marketplace</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Choose how you'd like to sign in.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row">
          <ChoiceCard
            icon={PawPrint}
            title="Pet Owner"
            description="Manage your pets' profiles, find services, and book care."
            cta="Continue as Pet Owner"
            onClick={() => navigate('/user/login')}
            testid="choose-user"
          />
          <ChoiceCard
            icon={Store}
            title="Vendor"
            description="Onboard your business and manage your service listings."
            cta="Continue as Vendor"
            onClick={() => navigate('/login')}
            testid="choose-vendor"
          />
        </div>
      </div>
    </div>
  );
}
